import type { TipusArea } from '@/api/types';
import type { DepartamentEstructura, TipusNode } from '../marc/estructura';
import { normalitza, type Seleccio } from './suport';

export interface FiltreArbre {
  text: string;
  tipus: TipusArea | '';
  inactius: boolean;
}

export interface NodeVisible {
  tipus: TipusNode;
  id: number;
  nom: string;
  nivell: number;
  actiu: boolean;
  tipusArea?: TipusArea;
  compte?: string;
  fills: NodeVisible[];
}

const actiu = (x: { actiu?: boolean }) => x.actiu !== false;

/**
 * Nodes visibles segons el filtre: els inactius s'amaguen (tret del seleccionat), el filtre de
 * tipus d'àrea s'aplica a àrees i serveis, i la cerca mostra els nodes que coincideixen amb els
 * seus avantpassats.
 */
export function nodesVisibles(
  deps: DepartamentEstructura[],
  f: FiltreArbre,
  seleccio: Seleccio | null,
  t: (k: string, o?: Record<string, unknown>) => string,
): NodeVisible[] {
  const q = normalitza(f.text.trim());
  const coincideix = (n: { nom: string; codi?: string | null }) =>
    !q || normalitza(`${n.nom} ${n.codi ?? ''}`).includes(q);
  const esSel = (tipus: TipusNode, id: number) => seleccio?.tipus === tipus && seleccio.id === id;
  const mostra = (tipus: TipusNode, n: { id: number; actiu?: boolean }) =>
    f.inactius || actiu(n) || esSel(tipus, n.id);

  return deps
    .filter((d) => mostra('DEPARTAMENT', d))
    .map((d): NodeVisible | null => {
      const depCoincideix = coincideix(d);
      const arees = d.arees
        .filter((a) => mostra('AREA', a) && (!f.tipus || a.tipus === f.tipus))
        .map((a): NodeVisible | null => {
          const areaCoincideix = depCoincideix || coincideix(a);
          const serveis = a.serveis
            .filter((s) => mostra('SERVEI', { id: s.id, actiu: s.actiu }))
            .filter((s) => areaCoincideix || coincideix(s))
            .map((s): NodeVisible => ({
              tipus: 'SERVEI',
              id: s.id,
              nom: s.nom,
              nivell: 3,
              actiu: s.actiu !== false,
              tipusArea: a.tipus,
              fills: [],
            }));
          if (!areaCoincideix && serveis.length === 0) return null;
          return {
            tipus: 'AREA',
            id: a.id,
            nom: a.nom,
            nivell: 2,
            actiu: actiu(a),
            tipusArea: a.tipus,
            compte: String(a.serveis.length),
            fills: serveis,
          };
        })
        .filter((x): x is NodeVisible => x !== null);
      if (f.tipus && arees.length === 0) return null;
      if (!depCoincideix && arees.length === 0) return null;
      const nServeis = d.arees.reduce((n, a) => n + a.serveis.length, 0);
      return {
        tipus: 'DEPARTAMENT',
        id: d.id,
        nom: d.nom,
        nivell: 1,
        actiu: actiu(d),
        compte: t('estructura.compteDep', { arees: d.arees.length, serveis: nServeis }),
        fills: arees,
      };
    })
    .filter((x): x is NodeVisible => x !== null);
}
